import React from 'react';
import { Check, Clapperboard } from 'lucide-react';
import { Reveal } from './Reveal';

const Services = () => {
  const packages = [
    {
      name: 'Exposure',
      price: '$750',
      featured: false,
      features: [
        '1 Long Horizontal Video (60-180 seconds)',
        '5 Vertical Teaser Videos (50 seconds or less)',
        '1 Background Video Banner',
        'Royalty Free Music',
        'Audio version & one without music',
        'All Video and Audio editing',
        'AI Enhancement Editing included',
        '1 Week turnaround'
      ],
      bestFor: 'Small businesses & content creators'
    },
    {
      name: 'Over Exposed',
      price: '$1,250',
      featured: false,
      features: [
        '2 Long Horizontal Videos (60-180 seconds)',
        '10 Vertical Teaser Videos (50 seconds or less)',
        '2 Background Video Banners',
        'Royalty Free Music',
        'Audio version & one without music',
        'All Video and Audio editing',
        'AI Enhancement Editing included',
        '2 Week turnaround'
      ],
      bestFor: 'Growing brands & marketing campaigns'
    },
    {
      name: 'Celebrity',
      price: '$2,500',
      featured: false,
      features: [
        '5 Long Horizontal Videos (60-180 seconds)',
        '20 Vertical Teaser Videos (50 seconds or less)',
        '3 Background Video Banners',
        'Royalty Free Music',
        'Audio version & one without music',
        'All Video and Audio editing',
        'AI Enhancement Editing included',
        '3 Week turnaround'
      ],
      bestFor: 'Enterprises & high-volume content needs'
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="section-padding bg-[#050505] border-t border-white/5" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center max-w-3xl mx-auto mb-16">
          <p className="overline mb-5">Packages &amp; Pricing</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6">
            Everything you need. <span className="gold-gradient">One team.</span>
          </h2>
          <p className="text-lg md:text-xl text-[#a0a0a0] leading-relaxed">
            Choose the perfect package for your video editing needs. All packages include professional
            editing, royalty-free music, and multiple format deliverables.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mt-12 items-stretch">
          {packages.map((pkg, index) => (
            <Reveal key={index} delay={index * 0.1} className="h-full">
              <div
                data-testid={`package-${pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="service-card h-full flex flex-col relative"
              >
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-semibold mb-3 text-white">{pkg.name}</h3>
                  <div className="font-display text-6xl font-semibold gold-gradient leading-none mb-3">{pkg.price}</div>
                  <p className="text-[#a0a0a0] text-sm">{pkg.bestFor}</p>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  {pkg.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#d4af37]/15 flex items-center justify-center mt-0.5">
                        <Check size={13} className="text-[#d4af37]" />
                      </div>
                      <span className="text-[#cfcfcf] text-sm leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={scrollToContact}
                  data-testid={`package-cta-${pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="cta-button-outline w-full"
                >
                  Get Started
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Add-on: On-site Directing */}
        <div className="mt-16 max-w-2xl mx-auto">
          <Reveal>
            <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-8 md:p-10 hover:border-[#d4af37]/40 transition-colors duration-500" data-testid="addon-directing">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 rounded-full bg-[#d4af37]/15 flex items-center justify-center flex-shrink-0">
                  <Clapperboard size={24} className="text-[#d4af37]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">On-Site Day of Shoot Directing</h3>
                  <p className="text-[#a0a0a0] text-sm">Professional direction for your shoot</p>
                </div>
              </div>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-display text-4xl font-semibold gold-gradient">$125</span>
                <span className="text-[#a0a0a0]">/ hour</span>
              </div>
              <p className="text-[#cfcfcf] text-sm leading-relaxed">
                Get hands-on professional direction during your shoot day. Our experienced team ensures every
                shot is captured perfectly, maximizing production value and minimizing reshoots.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 text-center">
          <p className="text-[#a0a0a0] mb-6">Need a custom package or have specific requirements?</p>
          <button onClick={scrollToContact} className="cta-button-outline" data-testid="custom-solutions-btn">
            Contact Us for Custom Solutions
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
